import type { Feature, FeatureCollection, GeoJSON, GeoJsonProperties, LineString, Point } from "geojson";

export type RouteEditorGeometry = Point | LineString;
export type RouteEditorFeature = Feature<RouteEditorGeometry, GeoJsonProperties>;

export interface RouteEditorEntry {
  key: string;
  originalIndex: number | null;
  feature: RouteEditorFeature;
}

export interface RouteEditorDocument {
  original: GeoJSON | null;
  entries: RouteEditorEntry[];
}

type PointRouteEditorEntry = RouteEditorEntry & { feature: Feature<Point, GeoJsonProperties> };

const isEditableGeometry = (geometry: GeoJSON): geometry is RouteEditorGeometry =>
  geometry.type === "Point" || geometry.type === "LineString";

const isEditableFeature = (feature: Feature): feature is RouteEditorFeature =>
  feature.geometry !== null && isEditableGeometry(feature.geometry);

export const canEditRouteGeoJson = (value: GeoJSON | null): boolean => {
  if (!value) return true;
  if (isEditableGeometry(value)) return true;
  return value.type === "FeatureCollection" && value.features.every(isEditableFeature);
};

export const createRouteEditorDocument = (value: GeoJSON | null, createKey: () => string): RouteEditorDocument => {
  if (!value) return { original: null, entries: [] };

  if (isEditableGeometry(value)) {
    return {
      original: value,
      entries: [
        {
          key: createKey(),
          originalIndex: null,
          feature: { type: "Feature", properties: null, geometry: value },
        },
      ],
    };
  }

  if (value.type !== "FeatureCollection" || !value.features.every(isEditableFeature)) {
    throw new Error("Unsupported route GeoJSON");
  }

  return {
    original: value,
    entries: value.features.map((feature, originalIndex) => ({ key: createKey(), originalIndex, feature })),
  };
};

export const pointEntries = (entries: RouteEditorEntry[]): PointRouteEditorEntry[] =>
  entries.filter((entry): entry is PointRouteEditorEntry => entry.feature.geometry.type === "Point");

export const createLineStringBetweenPoints = (
  entries: RouteEditorEntry[],
  pointKeys: [string, string] | string[],
): LineString | null => {
  if (pointKeys.length !== 2 || pointKeys[0] === pointKeys[1]) return null;
  const pointsByKey = new Map(pointEntries(entries).map((entry) => [entry.key, entry]));
  const startPoint = pointsByKey.get(pointKeys[0]);
  const endPoint = pointsByKey.get(pointKeys[1]);
  if (!startPoint || !endPoint) return null;

  return {
    type: "LineString",
    coordinates: [[...startPoint.feature.geometry.coordinates], [...endPoint.feature.geometry.coordinates]],
  };
};

const composeFeatureCollection = (document: RouteEditorDocument, entries: RouteEditorEntry[]): FeatureCollection => {
  const originalFeatures = document.original?.type === "FeatureCollection" ? document.original.features : [];
  const originalEntries = new Map(
    entries
      .filter((entry): entry is RouteEditorEntry & { originalIndex: number } => entry.originalIndex !== null)
      .map((entry) => [entry.originalIndex, entry.feature]),
  );
  const newFeatures = entries.filter((entry) => entry.originalIndex === null).map((entry) => entry.feature);
  const features = originalFeatures.flatMap((_, index) => {
    const feature = originalEntries.get(index);
    return feature ? [feature] : [];
  });

  return {
    ...(document.original?.type === "FeatureCollection" ? document.original : {}),
    type: "FeatureCollection",
    features: [...features, ...newFeatures],
  };
};

export const serializeRouteEditorDocument = (
  document: RouteEditorDocument,
): { document: RouteEditorDocument; geoJson: FeatureCollection } => ({
  document,
  geoJson: composeFeatureCollection(document, document.entries),
});
